function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useState, forwardRef, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { fontStyle, spacing, borderRadius, themeLight } from '@naf/theme';
import { nafColor } from '@nafcore/theme';
import { ExpandMore } from '@styled-icons/material/ExpandMore';
import { Cancel } from '@styled-icons/material/Cancel';
import { useCombobox } from 'downshift';
import Label from './label';
import { propValidationConfig } from '../propValidationConfig';
export const Select = /*#__PURE__*/forwardRef(({
  options,
  handleSelect,
  selected,
  placeholder,
  width = '256px',
  maxLines,
  error = false,
  disabled = false,
  ariaLabel,
  className,
  label,
  handleSearch,
  filterFunction,
  autoComplete,
  ...rest
}, ref) => {
  const [inputItems, setInputItems] = useState(options);
  const [hasInput, setHasInput] = useState(false);
  useEffect(() => {
    if (options) {
      setInputItems(options);
    }
  }, [options]);
  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
    openMenu,
    selectItem,
    getLabelProps
  } = useCombobox({
    itemToString: selectedItem => selectedItem && selectedItem.label || selectedItem && selectedItem.name || '',
    items: inputItems,
    onSelectedItemChange: ({
      selectedItem
    }) => {
      if (selectedItem) handleSelect(selectedItem);
    },
    onInputValueChange: ({
      inputValue
    }) => {
      const filteredOptions = !inputValue ? options : options.filter(item => item.label.toLowerCase().includes(inputValue.toLowerCase()));
      setInputItems(filteredOptions);
      if (filterFunction && filteredOptions.length === 0) {
        filterFunction(inputValue);
      }
      setHasInput(!!inputValue);
    }
  });
  useEffect(() => {
    if (selected) {
      selectItem(selected);
    }
  }, [selected, selectItem]);
  const handleKeyPress = event => {
    if (event.key === 'Enter') {
      if (handleSearch) {
        handleSearch(getInputProps().value);
      }
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, label && /*#__PURE__*/React.createElement(Label, getLabelProps(), label), /*#__PURE__*/React.createElement(Container, _extends({
    className: className,
    "aria-label": ariaLabel
  }, rest), /*#__PURE__*/React.createElement(ComboBox, _extends({
    width: width,
    open: isOpen,
    error: error,
    disabled: disabled
  }, getComboboxProps()), /*#__PURE__*/React.createElement(StyledInput, _extends({
    ref: ref,
    placeholder: placeholder
  }, getInputProps({
    onFocus: () => {
      if (!isOpen) openMenu();
      setInputItems(options);
    },
    onKeyDown: handleKeyPress
  }), {
    value: getInputProps().value,
    disabled: disabled,
    autoComplete: autoComplete
  })), hasInput && /*#__PURE__*/React.createElement(ClearButton, {
    type: "button",
    tabIndex: -1,
    onClick: () => {
      selectItem({
        value: '',
        label: ''
      });
    },
    "aria-label": "clear selection",
    disabled: disabled
  }, /*#__PURE__*/React.createElement(Cancel, {
    size: 16
  })), options.length > 0 && /*#__PURE__*/React.createElement(ExpandButton, _extends({
    type: "button",
    open: isOpen
  }, getToggleButtonProps({
    onFocus: () => {
      setInputItems(options);
    }
  }), {
    "aria-label": "toggle menu",
    disabled: disabled
  }), /*#__PURE__*/React.createElement(ExpandMore, {
    size: 24
  }))), /*#__PURE__*/React.createElement(ListContainer, {
    width: width,
    maxLines: maxLines
  }, /*#__PURE__*/React.createElement(StyledUl, getMenuProps(), isOpen && inputItems.map((item, index) => /*#__PURE__*/React.createElement(StyledLi, _extends({
    hover: highlightedIndex === index,
    key: `${item.value}${index}`
  }, getItemProps({
    item,
    index,
    disabled: !!item.disabled
  })), item.dropdownLabel || item.label))))));
});
const errorStyle = css(["border:2px solid;border-color:", ";padding-left:calc(", " - 1px);padding-right:calc(", " - 1px);"], ({
  theme
}) => theme.componentColors ? theme.componentColors.alert.error : themeLight.componentColors.alert.error, spacing.space12, spacing.space12);
const Container = styled.div.withConfig({
  displayName: "select__Container",
  componentId: "sc-1x2s4f4-0"
})(["display:flex;width:fit-content;max-width:100%;height:", ";box-sizing:border-box;position:relative;"], spacing.space48);
const ComboBox = styled.div.withConfig(propValidationConfig).withConfig({
  displayName: "select__ComboBox",
  componentId: "sc-1x2s4f4-1"
})(["display:flex;width:", ";max-width:100%;box-sizing:border-box;height:100%;border:1px solid;border-color:", ";border-radius:", ";padding:0 ", ";&:focus{border:2px solid;border-color:", ";padding-right:calc(", " - 1px);padding-left:calc(", " - 1px);}", " ", " &:invalid{", ";}&:disabled,&:disabled input{background-color:", ";border-color:", ";color:", ";-webkit-text-fill-color:", ";cursor:not-allowed;opacity:1;button{cursor:not-allowed;}}", ""], props => props.width || '256px', ({
  theme
}) => theme.border ? theme.border.heavy : themeLight.border.heavy, borderRadius.M, spacing.space12, ({
  theme
}) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText, spacing.space12, spacing.space12, ({
  open
}) => open && css(["border:2px solid;border-color:", ";padding-right:calc(", " - 1px);padding-left:calc(", " - 1px);"], ({
  theme
}) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText, spacing.space12, spacing.space12), ({
  error
}) => error && css(["", ";"], errorStyle), errorStyle, ({
  theme
}) => theme.componentColors ? theme.componentColors.inputElement.backgroundDisabled : themeLight.componentColors.inputElement.backgroundDisabled, ({
  theme
}) => theme.border ? theme.border.heavy : themeLight.border.heavy, ({
  theme
}) => theme.typography ? theme.typography.disabledText : themeLight.typography.disabledText, ({
  theme
}) => theme.typography ? theme.typography.disabledText : themeLight.typography.disabledText, ({
  disabled
}) => disabled && css(["background-color:", ";border-color:", ";color:", ";-webkit-text-fill-color:", ";cursor:not-allowed;opacity:1;input{background-color:", ";border-color:", ";color:", ";-webkit-text-fill-color:", ";cursor:not-allowed;opacity:1;}button{cursor:not-allowed;}"], ({
  theme
}) => theme.componentColors ? theme.componentColors.inputElement.backgroundDisabled : themeLight.componentColors.inputElement.backgroundDisabled, ({
  theme
}) => theme.border ? theme.border.heavy : themeLight.border.heavy, ({
  theme
}) => theme.typography ? theme.typography.disabledText : themeLight.typography.disabledText, ({
  theme
}) => theme.typography ? theme.typography.disabledText : themeLight.typography.disabledText, ({
  theme
}) => theme.componentColors ? theme.componentColors.inputElement.backgroundDisabled : themeLight.componentColors.inputElement.backgroundDisabled, ({
  theme
}) => theme.border ? theme.border.heavy : themeLight.border.heavy, ({
  theme
}) => theme.typography ? theme.typography.disabledText : themeLight.typography.disabledText, ({
  theme
}) => theme.typography ? theme.typography.disabledText : themeLight.typography.disabledText));
const StyledInput = styled.input.withConfig({
  displayName: "select__StyledInput",
  componentId: "sc-1x2s4f4-2"
})(["display:flex;width:100%;border:none;outline:none;font:inherit;&:focus:not([disabled]),&:active:not([disabled]),&:focus-visible:not([disabled]){border:none;outline:none;}::placeholder{color:", ";}"], ({
  theme
}) => theme.typography ? theme.typography.subtleText : themeLight.typography.subtleText);
const StyledButton = css(["border:none;margin:0;padding:0;width:auto;overflow:visible;background:transparent;color:inherit;font:inherit;line-height:normal;touch-action:manipulation;appearance:none !important;display:flex;align-items:center;cursor:pointer;"]);
const ExpandButton = styled.button.withConfig(propValidationConfig).withConfig({
  displayName: "select__ExpandButton",
  componentId: "sc-1x2s4f4-3"
})(["", ";margin-left:", ";", " ", ""], StyledButton, spacing.space8, ({
  open
}) => open && css(["svg{transform:rotate(180deg);transition-duration:0.3s;}"]), ({
  open
}) => !open && css(["svg{transform:rotate(0);transition-duration:0.3s;}"]));
const ClearButton = styled.button.withConfig({
  displayName: "select__ClearButton",
  componentId: "sc-1x2s4f4-4"
})(["", ";color:", ";"], StyledButton, ({
  theme
}) => theme.border ? theme.border.heavy : themeLight.border.heavy);
const ListContainer = styled.div.withConfig(propValidationConfig).withConfig({
  displayName: "select__ListContainer",
  componentId: "sc-1x2s4f4-5"
})(["width:", ";position:absolute;left:1px;top:", ";background:", ";box-shadow:0 2px 12px rgba(0,0,0,0.24);border-radius:", ";overflow-y:auto;z-index:99;", ""], props => props.width || '256px', spacing.space56, ({
  theme
}) => theme.background ? theme.background.default : themeLight.background.default, borderRadius.M, ({
  maxLines
}) => maxLines && css(["max-height:calc(", " * ", ");"], spacing.space48, maxLines));
const StyledUl = styled.ul.withConfig({
  displayName: "select__StyledUl",
  componentId: "sc-1x2s4f4-6"
})(["list-style:none;padding:0;margin:0;"]);
const StyledLi = styled.li.withConfig(propValidationConfig).withConfig({
  displayName: "select__StyledLi",
  componentId: "sc-1x2s4f4-7"
})(["display:flex;align-items:center;list-style:none;width:100%;text-align:start;white-space:pre-wrap;border:0;padding:0 ", ";box-sizing:border-box;appearance:none;height:", ";background:transparent;", ";cursor:pointer;", ""], spacing.space12, spacing.space48, fontStyle.bodyText.bodyText, ({
  hover
}) => hover && css(["background:", ";"], nafColor.signature.yellow10));
//# sourceMappingURL=select.js.map